import type { ColDef } from "ag-grid-community";
import i18n from "i18next";
import k from "src/i18n/keys";
import type { Group, UID, UsersById } from "src/types";
import {
  CreatorCellRenderer,
  MembersCellRenderer,
  OwnersCellRenderer,
  TitleCellRenderer,
} from "./cellRenderers";

interface GetColumnsParams {
  groupsById?: Record<string, Group>;
  usersById?: UsersById;
}

export const getColumns = ({
  groupsById,
  usersById,
}: GetColumnsParams): Array<ColDef> => {
  const getGroupById = (id?: number): Group | undefined => {
    if (!id || !groupsById) return undefined;
    return groupsById[String(id)];
  };

  const getMembers = (id?: number): Array<UID> => {
    const group = getGroupById(id);
    return group?.members || [];
  };

  const getOwners = (id?: number): Array<UID> => {
    const group = getGroupById(id);
    return group?.owners || [];
  };

  const getCreatorDisplayValue = (id?: number): string => {
    const group = getGroupById(id);
    if (!group) return "";

    const creatorUid = group.creator;
    const creator = (
      usersById as
        | Record<string, { displayName?: string; email?: string }>
        | undefined
    )?.[creatorUid];

    return creator?.displayName || creator?.email || creatorUid || "";
  };

  return [
    {
      headerName: i18n.t(k.GROUP_NAME1),
      field: "title",
      flex: 2.8,
      minWidth: 200,
      cellRenderer: TitleCellRenderer,
      valueGetter: (params) => getGroupById(params.data?.id)?.title || "",
      getQuickFilterText: (params) =>
        getGroupById(params.data?.id)?.title || "",
      sortable: true,
      filter: false,
    },
    {
      headerName: i18n.t(k.PARTICIPANTS),
      field: "members",
      flex: 1.4,
      minWidth: 150,
      cellRenderer: MembersCellRenderer,
      valueGetter: (params) => getMembers(params.data?.id),
    },
    {
      headerName: i18n.t(k.GROUP_OWNERS1),
      field: "owners",
      flex: 1.4,
      minWidth: 150,
      cellRenderer: OwnersCellRenderer,
      valueGetter: (params) => getOwners(params.data?.id),
    },
    {
      headerName: i18n.t(k.CREATED_BY2),
      field: "creator",
      flex: 1.4,
      minWidth: 150,
      cellRenderer: CreatorCellRenderer,
      valueGetter: (params) => getCreatorDisplayValue(params.data?.id),
      getQuickFilterText: (params) => getCreatorDisplayValue(params.data?.id),
    },
  ];
};
